// src/pages/CartPage.jsx
import React, { useEffect, useState, useRef, useMemo } from "react";
import { useCart } from "../cart/CartContext";
import { Link, useNavigate } from "react-router-dom";
import {
  FaHome,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaUserCircle,
  FaTag,
} from "react-icons/fa";

import Lottie from "lottie-react";
import shoppingAnim from "../assets/Shopping.json";

import { auth, db } from "../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

import "../styles/CartPage.css";
import "../styles/WeddingPage.css"; // navbar styles
import api from "../api";

/** Services menu links */
const servicesLinks = [
  { to: "/wedding", label: "Wedding Planning" },
  { to: "/design", label: "Design & Decoration" },
  { to: "/destination", label: "Destination Wedding Venues" },
  { to: "/entertainment", label: "Entertainment" },
  { to: "/others", label: "Custom Events" },
];

/** Razorpay loader */
function loadRazorpay() {
  return new Promise((resolve, reject) => {
    if (typeof window !== "undefined" && window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error("Razorpay SDK failed to load"));
    document.body.appendChild(s);
  });
}

export default function CartPage() {
  const { cart, removeFromCart, clearCart } = useCart();
  const navigate = useNavigate();

  // Navbar (match Profile)
  const [dropdown, setDropdown] = useState(false);          // desktop Services
  const [mobileDrop, setMobileDrop] = useState(false);      // mobile Services dropdown in navbar
  const [navOpen, setNavOpen] = useState(false);            // mobile sidebar
  const dropdownTimeout = useRef(null);

  // Auth
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [mobile, setMobile] = useState("");
  const [showMobileModal, setShowMobileModal] = useState(false);
  const [mobileInput, setMobileInput] = useState("");
  const [pendingCheckout, setPendingCheckout] = useState(false);

  // Coupons / totals
  const [coupon, setCoupon] = useState("");
  const [couponMsg, setCouponMsg] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setChecking(false);
      if (firebaseUser) {
        const ref = doc(db, "users", firebaseUser.uid);
        const snap = await getDoc(ref);
        if (snap.exists() && snap.data().mobile) setMobile(snap.data().mobile);
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!checking && !user) navigate("/login");
  }, [checking, user, navigate]);

  const subtotal = useMemo(
    () => cart.reduce((sum, it) => sum + (Number(it.price) || 0), 0),
    [cart]
  );

  const discount = useMemo(() => {
    if (!appliedCoupon) return 0;
    if (appliedCoupon === "FIRST10") return Math.round(subtotal * 0.1);
    if (appliedCoupon === "SAVE500") return 500;
    if (appliedCoupon === "EVENTLAUNCH") return Math.round(subtotal * 0.10); // 10% off for new event listings
    if (appliedCoupon === "VENUEBOOK") return 750; // Flat 750 discount on venue booking fees
    if (appliedCoupon === "TICKETBULK") return Math.round(subtotal * 0.15); // 15% off on bulk ticket purchases
    if (appliedCoupon === "FIRSTEVENT") return Math.round(subtotal * 0.20); // 20% off for a user's first event creation
    if (appliedCoupon === "PROMOTECP") return 500; // Flat 500 discount on event promotion packages
    if (appliedCoupon === "DECORBUNDLE") return Math.round(subtotal * 0.12); // 12% off on event decoration bundles
    if (appliedCoupon === "PHOTOGRAPHER500") return 500; // 500 fixed discount on photography services
    if (appliedCoupon === "WORKSHOPONLY") return Math.round(subtotal * 0.25); // 25% off specific workshop event tickets
    if (appliedCoupon === "EARLYBIRD") return Math.round(subtotal * 0.10); // 10% off for early bird event registration
    if (appliedCoupon === "SPONSORBONUS") return 1200; // Flat 1200 discount for new event sponsors
    return 0;
  }, [appliedCoupon, subtotal]);

  const convenienceFee = subtotal > 0 ? 49 : 0;
  const payable = Math.max(subtotal - discount + convenienceFee, 0);

  async function saveMobileToFirestore(uid, mobileNumber) {
    await setDoc(doc(db, "users", uid), { mobile: mobileNumber }, { merge: true });
    setMobile(mobileNumber);
  }

  function applyCoupon() {
    const code = coupon.trim().toUpperCase();
    const validCoupons = [
      "FIRST10", "SAVE500", "EVENTLAUNCH", "VENUEBOOK", "TICKETBULK",
      "FIRSTEVENT", "PROMOTECP", "DECORBUNDLE", "PHOTOGRAPHER500",
      "WORKSHOPONLY", "EARLYBIRD", "SPONSORBONUS"
    ];
    
    if (!code) { setCouponMsg("Enter a coupon code."); return; }
    if (validCoupons.includes(code)) {
      setAppliedCoupon(code);
      setCouponMsg(`Coupon ${code} applied.`);
    } else {
      setAppliedCoupon(null);
      setCouponMsg("Invalid code.");
    }
  }
  function clearCoupon() {
    setAppliedCoupon(null);
    setCoupon("");
    setCouponMsg("Coupon removed.");
  }

  async function startRazorpayCheckout(_mobile) {
    if (isProcessing) return; // Prevent double clicks
    setIsProcessing(true);
    
    try {
      // Show loading overlay with animation
      const overlay = document.createElement('div');
      overlay.id = 'payment-loading-overlay';
      overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99998;display:flex;align-items:center;justify-content:center;backdrop-filter:blur(4px);';
      
      const loadingBox = document.createElement('div');
      loadingBox.style.cssText = 'background:#fff;padding:40px 48px;border-radius:16px;box-shadow:0 12px 48px rgba(0,0,0,0.2);text-align:center;max-width:90%;';
      
      // Spinner animation
      const spinner = document.createElement('div');
      spinner.style.cssText = 'width:60px;height:60px;border:4px solid #f3f3f3;border-top:4px solid #b0354c;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 20px;';
      
      const title = document.createElement('div');
      title.id = 'payment-loading-title';
      title.style.cssText = 'font-size:20px;color:#b0354c;font-weight:700;margin-bottom:8px;font-family:inherit;';
      title.textContent = 'Initializing Payment';
      
      const subtitle = document.createElement('div');
      subtitle.id = 'payment-loading-subtitle';
      subtitle.style.cssText = 'font-size:15px;color:#666;font-family:inherit;';
      subtitle.innerHTML = 'Loading payment gateway<span class="loading-dots"></span>';
      
      const dots = document.createElement('style');
      dots.textContent = `
        .loading-dots::after {
          content: '';
          animation: dots 1.5s steps(4, end) infinite;
        }
        @keyframes dots {
          0%, 20% { content: ''; }
          40% { content: '.'; }
          60% { content: '..'; }
          80%, 100% { content: '...'; }
        }
      `;
      document.head.appendChild(dots);
      
      // Add keyframe animations
      if (!document.getElementById('spinner-keyframes')) {
        const style = document.createElement('style');
        style.id = 'spinner-keyframes';
        style.textContent = `
          @keyframes spin { 
            0% { transform: rotate(0deg); } 
            100% { transform: rotate(360deg); } 
          }
        `;
        document.head.appendChild(style);
      }
      
      loadingBox.appendChild(spinner);
      loadingBox.appendChild(title);
      loadingBox.appendChild(subtitle);
      overlay.appendChild(loadingBox);
      document.body.appendChild(overlay);
      
      // Update message for Razorpay loading
      await loadRazorpay();
      const titleEl = document.getElementById('payment-loading-title');
      const subtitleEl = document.getElementById('payment-loading-subtitle');
      if (titleEl) titleEl.textContent = 'Connecting to Payment Gateway';
      if (subtitleEl) subtitleEl.innerHTML = 'Creating your order<span class="loading-dots"></span>';
      
      const { data: orderData } = await api.post("/payment/order", { amount: payable });
      
      // Update message before opening Razorpay
      if (titleEl) titleEl.textContent = 'Opening Payment Window';
      if (subtitleEl) subtitleEl.innerHTML = 'Please complete your payment<span class="loading-dots"></span>';
      
      // Small delay to show the message
      await new Promise(resolve => setTimeout(resolve, 500));
      
      document.getElementById('payment-loading-overlay')?.remove();
      setIsProcessing(false);
      
      if (!orderData?.id) throw new Error(orderData?.error || "Order creation failed");

      const RZP_KEY =
        process.env.REACT_APP_RAZORPAY_KEY_ID ||
        process.env.REACT_APP_RAZORPAY_KEY;
      if (!RZP_KEY) {
        throw new Error("Razorpay key is not configured");
      }

      const options = {
        key: RZP_KEY,
        order_id: orderData.id,
        name: "Parivar Wedding Event",
        description: `Booking Payment (₹${payable.toLocaleString()})`,
        theme: { color: "#b0354c" },
        prefill: { name: user?.displayName || "Guest", email: user?.email || "guest@example.com", contact: _mobile },
        method: { card: true, upi: true, netbanking: true, wallet: false, emi: false, paylater: false },
        handler: async (resp) => {
          try {
            const { data: verify } = await api.post("/payment/verify-payment", resp);
            if (!verify?.verified) { alert("Verification failed. Contact support."); return; }
            await api.post("/testbooking", {
              uid: user?.uid,
              bookingData: {
                email: user?.email,
                mobile: _mobile,
                cart,
                subtotal,
                discount,
                convenienceFee,
                total: payable,
                paymentId: resp.razorpay_payment_id,
                orderId: resp.razorpay_order_id,
                signature: resp.razorpay_signature,
                date: new Date().toISOString(),
                coupon: appliedCoupon || null,
              },
            });
            clearCart();
            navigate("/success");
          } catch (e) {
            console.error(e);
            alert("Payment done, but saving failed. Please contact support.");
          }
        },
        modal: { ondismiss: () => console.log("Checkout closed") },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => alert(r?.error?.description || "Payment failed."));
      rzp.open();
    } catch (err) {
      console.error(err);
      document.getElementById('payment-loading-overlay')?.remove();
      setIsProcessing(false);
      
      // Better error messages
      let errorMsg = "Payment setup failed. ";
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMsg += "The server is taking too long to respond. Please check your internet connection and try again.";
      } else if (err.response?.status === 500) {
        errorMsg += "Server error. Please try again in a moment.";
      } else if (err.response?.data?.error) {
        errorMsg += err.response.data.error;
      } else {
        errorMsg += err.message || "Unknown error";
      }
      
      alert(errorMsg);
    }
  }

  function handleCheckout() {
    if (!user) return alert("Please login to continue.");
    if (payable <= 0) return alert("Cart total is zero.");
    if (mobile && /^[6-9]\d{9}$/.test(mobile)) startRazorpayCheckout(mobile);
    else { setShowMobileModal(true); setPendingCheckout(true); }
  }

  async function handleMobileSubmit(e) {
    e.preventDefault();
    const _mobile = mobileInput.trim();
    if (!/^[6-9]\d{9}$/.test(_mobile)) { alert("Enter a valid 10-digit Indian mobile."); return; }
    await saveMobileToFirestore(user.uid, _mobile);
    setShowMobileModal(false);
    setMobileInput("");
    if (pendingCheckout) { setPendingCheckout(false); startRazorpayCheckout(_mobile); }
  }

  if (checking) return <div className="checking-screen">Checking authentication...</div>;

  return (
    <div className="cart-page" style={{ overflowX: "hidden" }}>
      {/* ===== NAVBAR (same as Profile). Mobile shows a Services button too. ===== */}
      <header className="desktop-navbar">
        <div className="navbar-container">
          <div
            className="navbar-brand"
            onClick={() => navigate("/")}
            style={{ fontFamily: "'Great Vibes', cursive" }}
          >
            Parivar
          </div>

          {/* Desktop center nav */}
          <nav className="center-nav">
            <Link to="/" className="nav-link">
              <FaHome /> Home
            </Link>

            <div
              className="dropdown"
              onMouseEnter={() => { clearTimeout(dropdownTimeout.current); setDropdown(true); }}
              onMouseLeave={() => { dropdownTimeout.current = setTimeout(() => setDropdown(false), 200); }}
            >
              <button className="dropdown-btn">
                Services <FaChevronDown />
              </button>
              <div className={`dropdown-menu ${dropdown ? "show" : ""}`}>
                {servicesLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="dropdown-link"
                    onClick={() => setDropdown(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          {/* Right: Mobile Services quick button + Profile */}
          <div className="nav-right">
            {/* Mobile-only Services button with dropdown (mirrors desktop) */}
            <div className="dropdown mobile-only">
              <button
                className="dropdown-btn"
                onClick={() => setMobileDrop((v) => !v)}
                aria-expanded={mobileDrop ? "true" : "false"}
              >
                Services <FaChevronDown />
              </button>
              <div className={`dropdown-menu ${mobileDrop ? "show" : ""}`}>
                {servicesLinks.map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="dropdown-link"
                    onClick={() => setMobileDrop(false)}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>

            {/* Profile button (replaces cart on this page) */}
            <Link to="/profile" className="cart-link" title="Profile">
              {user?.photoURL ? (
                <img
                  src={user.photoURL}
                  alt="Me"
                  style={{ width: 24, height: 24, borderRadius: "50%", objectFit: "cover" }}
                />
              ) : (
                <FaUserCircle size={18} />
              )}
            </Link>
          </div>

          {/* Mobile menu icon for full sidebar */}
          <div className="mobile-menu-icon" onClick={() => setNavOpen(true)}>
            <FaBars size={22} />
          </div>
        </div>
      </header>

      {/* ===== MOBILE SIDEBAR (now uses .show + .open and high z-index) ===== */}
      {navOpen && (
        <div className="sidebar-overlay show" onClick={() => setNavOpen(false)}>
          <div className="sidebar open" onClick={(e) => e.stopPropagation()}>
            <div className="sidebar-header">
              <span>Menu</span>
              <FaTimes onClick={() => setNavOpen(false)} />
            </div>
            <div className="sidebar-link" onClick={() => { navigate("/"); setNavOpen(false); }}>
              <FaHome /> Home
            </div>
            <div className="sidebar-link" onClick={() => setDropdown((d) => !d)}>
              <FaChevronDown /> Services
            </div>
            {dropdown && (
              <div className="sidebar-submenu">
                {servicesLinks.map((link) => (
                  <div
                    key={link.to}
                    className="sidebar-submenu-item"
                    onClick={() => { navigate(link.to); setNavOpen(false); }}
                  >
                    {link.label}
                  </div>
                ))}
              </div>
            )}
            <div className="sidebar-link" onClick={() => { navigate("/profile"); setNavOpen(false); }}>
              <FaUserCircle /> Profile
            </div>
          </div>
        </div>
      )}

      {/* ===== CART CONTENT (unchanged) ===== */}
      <div id="cartGrid" style={{ maxWidth: 1100, margin: "120px auto 40px", padding: "0 16px" }}>
        <div className="cart-left card-like" style={{ minWidth: 0 }}>
          <h2 className="cart-title" style={{ marginTop: 0 }}>Your Selected Packages</h2>

          {cart.length === 0 ? (
            <div className="cart-empty">
              <div style={{ maxWidth: 360, width: "100%", margin: "0 auto" }}>
                <Lottie animationData={shoppingAnim} loop autoplay />
              </div>
              <h2>Your cart is empty!</h2>
              <p className="muted">Browse our services and add items to your cart.</p>
              <div className="cart-empty-buttons">
                <button onClick={() => navigate("/#services")}>Browse Services</button>
                <button onClick={() => navigate("/")}>Go Home</button>
              </div>
            </div>
          ) : (
            <ul className="cart-list">
              {cart.map((item) => (
                <li key={item.id} className="cart-item">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="cart-img"
                    onError={(e) => { e.currentTarget.src = "https://via.placeholder.com/120x80.png?text=Parivar Wedding Event"; }}
                  />
                  <div className="cart-item-info">
                    <strong className="item-name">{item.name}</strong>
                    {item.description && <p className="item-desc">{item.description}</p>}
                    <div className="cart-price">₹{Number(item.price || 0).toLocaleString()}</div>
                  </div>
                  <button onClick={() => removeFromCart(item.id)} className="cart-remove">Remove</button>
                </li>
              ))}
            </ul>
          )}
        </div>


        <aside className="cart-right card-like" style={{ minWidth: 0 }}>
          <h3 style={{ marginTop: 0 }}>Order Summary</h3>

          <div className="coupon-row">
            <div className="coupon-input">
              <FaTag />
              <input
                type="text"
                placeholder="Enter Your Coupon Code"
                value={coupon}
                onChange={(e) => { setCoupon(e.target.value); setCouponMsg(""); }}
              />
            </div>
            {appliedCoupon ? (
              <button className="btn-secondary" onClick={clearCoupon}>Remove</button>
            ) : (
              <button className="btn-primary" onClick={applyCoupon}>Apply</button>
            )}
          </div>
          {couponMsg && <div className="coupon-msg">{couponMsg}</div>}

          <div className="summary-rows">
            <div className="row"><span>Subtotal</span><span>₹{subtotal.toLocaleString()}</span></div>
            <div className="row"><span>Discount {appliedCoupon ? `(${appliedCoupon})` : ""}</span><span>- ₹{discount.toLocaleString()}</span></div>
            <div className="row"><span>Convenience Fee</span><span>₹{convenienceFee.toLocaleString()}</span></div>
            <div className="row total"><span>Payable</span><span>₹{payable.toLocaleString()}</span></div>
          </div>

          <div className="summary-actions">
            <button 
              onClick={handleCheckout} 
              className="checkout-btn" 
              disabled={payable <= 0 || cart.length === 0 || isProcessing}
            >
              {isProcessing ? "Processing..." : "Proceed to Payment"}
            </button>
            <button onClick={clearCart} className="clear-btn" disabled={cart.length === 0 || isProcessing}>Clear Cart</button>
          </div>

          <div className="note small muted">You’ll be redirected to Razorpay’s secure checkout. A payment receipt will be available after success.</div>
        </aside>
      </div>

      {/* Mobile number modal */}
      {showMobileModal && (
        <div className="modal-overlay">
          <form onSubmit={handleMobileSubmit} className="modal-form">
            <h3>Mobile Number Required</h3>
            <p>Please enter your 10-digit mobile number for order confirmation and payment.</p>
            <input
              type="tel"
              placeholder="Enter 10-digit mobile"
              value={mobileInput}
              maxLength={10}
              autoFocus
              pattern="[6-9][0-9]{9}"
              required
              onChange={(e) => setMobileInput(e.target.value.replace(/[^0-9]/g, ""))}
            />
            <button type="submit" className="modal-submit">Continue to Pay</button>
            <button type="button" className="modal-cancel" onClick={() => { setShowMobileModal(false); setPendingCheckout(false); }}>Cancel</button>
          </form>
        </div>
      )}

      {/* Navbar/mobile helpers (no style conflicts) */}
      <style>{`
        /* Make sure mobile Services button only shows on small screens */
        .mobile-only { display: none; }
        @media (max-width: 900px){
          .center-nav { display: none !important; }
          .mobile-menu-icon { display: inline-block !important; }
          .mobile-only { display: inline-block !important; }
        }
        /* Sidebar must sit above everything */
        .sidebar-overlay { z-index: 9998; }
        .sidebar { z-index: 9999; }
        /* Cart grid layout (unchanged) */
        #cartGrid{ display:grid; grid-template-columns:1fr; gap:24px; }
        @media (min-width:1025px){
          #cartGrid{ grid-template-columns: minmax(360px,1fr) 360px; }
          .cart-right{ position:sticky; top:104px; height:fit-content; }
        }
        /* A few list tweaks here to avoid CSS file conflicts */
        .card-like{ background:#fff; border:1px solid #f1f1f1; border-radius:20px; box-shadow:0 14px 40px rgba(0,0,0,.06); padding:24px; }
        .cart-list{ list-style:none; padding:0; margin:0; display:grid; gap:12px; }
        .cart-item{ display:grid; grid-template-columns:96px 1fr auto; gap:12px; border:1px solid #f1f1f1; border-radius:12px; padding:12px; align-items:center; background:#fff; }
        .cart-img{ width:96px; height:72px; object-fit:cover; border-radius:10px; background:#fafafa; border:1px solid #eee; }
        @media (max-width:900px){ .cart-item{ grid-template-columns:84px 1fr auto } .cart-img{ width:84px;height:64px } }
        @media (max-width:420px){ .cart-item{ grid-template-columns:72px 1fr } }
      `}</style>
    </div>
  );
}
